import { useEffect, useRef, useState } from "react";
import { Camera, ArrowClockwise, UploadSimple } from "@phosphor-icons/react";

export function CameraCapture({ onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(false);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setActive(false);
  };

  const start = async () => {
    setError(false);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s;
      setActive(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 50);
    } catch (e) {
      setError(true);
    }
  };

  useEffect(() => () => stop(), []);

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 640;
    c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      const file = new File([blob], `acceso_${Date.now()}.jpg`, { type: "image/jpeg" });
      setPreview(URL.createObjectURL(blob));
      onCapture(file);
      stop();
    }, "image/jpeg", 0.85);
  };

  const onFile = (e) => {
    const f = e.target.files[0];
    if (f) { setPreview(URL.createObjectURL(f)); onCapture(f); }
  };

  const retake = () => { setPreview(null); onCapture(null); };

  if (preview) {
    return (
      <div>
        <img src={preview} alt="captura" className="w-full aspect-[4/3] object-cover rounded-sm border border-border" />
        <button type="button" onClick={retake} data-testid="retake-btn"
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowClockwise size={16} /> Tomar otra
        </button>
      </div>
    );
  }

  return (
    <div>
      {active ? (
        <div>
          <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-[4/3] object-cover rounded-sm bg-black" />
          <button type="button" onClick={snap} data-testid="snap-btn"
            className="mt-2 w-full h-11 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 inline-flex items-center justify-center gap-2">
            <Camera size={18} weight="bold" /> Capturar foto
          </button>
        </div>
      ) : (
        <div className="border border-dashed border-input rounded-sm p-6 text-center">
          <Camera size={32} weight="light" className="mx-auto text-muted-foreground mb-2" />
          <button type="button" onClick={start} data-testid="start-camera-btn"
            className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 text-white rounded-sm font-medium hover:bg-slate-800 transition-colors duration-200">
            <Camera size={18} weight="bold" /> Abrir cámara
          </button>
          {error && <p className="text-xs text-danger mt-2">No se pudo abrir la cámara. Usa la opción de archivo.</p>}
          <div className="mt-3 text-xs text-muted-foreground">o</div>
          <label className="mt-2 inline-flex items-center gap-2 h-10 px-4 border border-input rounded-sm font-medium cursor-pointer hover:bg-muted transition-colors duration-200">
            <UploadSimple size={18} /> Subir archivo
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="camera-file-input" />
          </label>
        </div>
      )}
    </div>
  );
}
